import { Box, Dialog, DialogContent, Grid, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEffect, useState } from "react";
import { DocumentRepoService } from "../../../Component/Api/DocumentRepo/DocumentRepoService";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { DocumentRepo } from "../../../Component/Model/Dto/EmploymentEquity/DocumentRepo";
import { MDBox } from "../../../Component/Common/Components/MDBox";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { MDButton } from "../../../Component/Common/Components/MDButton";
import AppTextInput from "../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { LoadingButton } from "@mui/lab";
import { FieldValues, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { addSectionValidation } from "./AddSectionValidation";
import AddIcon from '@mui/icons-material/Add';
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { DocumentRepoSection } from "./DocumentRepoSection";
import { useHistory } from "react-router-dom";
import { BEEScorecardService } from "../../../Component/Api/BEEScorecard/BEEScorecard";
import { ScorecardLicenseSearchRequest } from "../../../Component/Model/Dto/ScorecardLicense";
import { EE123InterestModal } from "../EE123InterestModal";
import { DocumentRepoSectionModuleTypes, UserActivityActionType } from "../../../Component/Model/Enum/BEE123Enums";
import { UserActivityService } from "../../../Component/Api/UserActivity/UserActivityService";
import { DocumentRepositorySection } from "../../../Component/Common/Constants/UserActivityTrackingSections";

interface IProps{
    documentRepoSectionModuleType : DocumentRepoSectionModuleTypes;
}

export const DocumentRepoBasePage = (props : IProps) => {
    const [openAddOrEditSectionDialog, setOpenAddOrEditSectionDialog] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [documentSections, setDocumentSections] = useState<DocumentRepo.Section[]>([]);
    const [selectedSectionId, setSelectedSectionId] = useState<number>(0);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    
    const [showInterestModal, setShowInterestModal] = useState<boolean>(false);
    const history = useHistory();
    
    const { control, handleSubmit, setValue, resetField, formState: { isSubmitting } } = useForm({
        mode: 'all',
        resolver: yupResolver(addSectionValidation)
    });

    const scorecardLicenseSearchRequest: ScorecardLicenseSearchRequest = {
        Draw: 2,
        PageNumber: 1,
        PageSize: 10,
        SortColumnNames: ['ScorecardTypeName'],
        SortDirections: ['Ascending'],
        SearchValue: "",
        ConsolidationValues: null,
        IsGroup: false,
        ScorecardTypes: ["43"]
    };

    const checkScorecardLicense = async () => {
        setIsLoading(true);
        try {
            const scorecardSummaryResult = await BEEScorecardService.getScorecardLicenseSummaryData(scorecardLicenseSearchRequest);
            if (scorecardSummaryResult.data.dataObject.length === 0) {
                setShowInterestModal(true);
            }

        } catch (error) {
            console.log(error);

        } finally {
            setIsLoading(false);
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const searchResult = await DocumentRepoService.Search(props.documentRepoSectionModuleType);
            if (searchResult.data) {
                setDocumentSections(searchResult.data.Sections);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    }

    const sectionLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: DocumentRepositorySection,
            actionType: actionType,
            actionDescription: actionDescription,
            requestUrl: window.location.href
        });
    }

    const addOrEditSection = async (data: FieldValues) => {

        const section: DocumentRepo.AddOrEditSectionDtoOut = {
            Id: selectedSectionId,
            Name: data.Name,
            SectionModuleType: props.documentRepoSectionModuleType
        };

        setIsLoading(true);
        try {
            await DocumentRepoService.AddOrEditDocumentSection(section);
            sectionLog(selectedSectionId === 0 ? UserActivityActionType.Create : UserActivityActionType.Update, selectedSectionId === 0 ? "Add section" : "Edit section");
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
        fetchData();
        closeModal();
    }

    const deleteSection = async (id: number) => {
        setSelectedSectionId(id);
        setShowConfirmationDialog(true);
    }

    const onDelete = async () => {
        setShowConfirmationDialog(false);
        setIsLoading(true);
        try {
            await DocumentRepoService.DeleteDocumentSection(selectedSectionId);
            sectionLog(UserActivityActionType.Delete, "Delete a section");
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
        fetchData();
    }

    const editSection = (id: number, sectionName: string) => {
        setSelectedSectionId(id);
        setValue("Name", sectionName);
        setOpenAddOrEditSectionDialog(true);
    }

    const closeModal = () => {
        resetField("Name");
        setSelectedSectionId(0);
        setOpenAddOrEditSectionDialog(false);
        fetchData();
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectedSectionId(0);
    }

    useEffect(() => {
        fetchData();
        if (props.documentRepoSectionModuleType === DocumentRepoSectionModuleTypes.EE_Module){
            checkScorecardLicense();
        }
    }, []);

    if (isLoading) {
        return (<SmallLoading />);
    }
    else {
        return (
            <>
                <Grid container style={{ padding: "10px" }}>
                    <MDBox margin="15px" marginBottom="20px" marginTop="0px">
                        <MDTypography variant='h4' fontWeight='bold'>
                            Document Repository
                        </MDTypography>
                    </MDBox>
                    {
                        documentSections?.map((item: DocumentRepo.Section) => {
                            return (
                                <Grid item sm={12} md={12} lg={12} xl={12} >
                                    <MDBox sx={{ background: 'white', borderRadius: '16px', margin: "15px" }}>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDBox style={{ margin: "15px", paddingTop: "10px" }}>
                                                <MDTypography variant="h4" >
                                                    {item.SectionName}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox display="flex">
                                                <IconButton aria-label="edit" color="primary" onClick={() => editSection(item.Id, item.SectionName)} >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton aria-label="delete" color="error" onClick={() => deleteSection(item.Id)} >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox >
                                            <DocumentRepoSection documentData={item.Documents} sectionId={item.Id} sectionName={item.SectionName} refreshData={fetchData} />
                                        </MDBox>
                                    </MDBox>
                                </Grid>
                            )
                        })
                    }
                    <Grid item sm={12} md={12} lg={12} xl={12} >
                        <MDBox margin="15px">
                            <MDButton variant="contained" color="secondary" onClick={() => { setOpenAddOrEditSectionDialog(true) }} startIcon={<AddIcon />}>Add Section </MDButton>
                        </MDBox>
                    </Grid>
                </Grid>
                <Dialog open={openAddOrEditSectionDialog} onClose={() => { closeModal() }} aria-labelledby="form-dialog-title" maxWidth='sm' disableEnforceFocus fullWidth>
                    <CustomDialogTitle id="customized-dialog-title" onClose={() => closeModal()}>
                        {selectedSectionId === 0 ? 'Add Section' : 'Edit Section'}
                    </CustomDialogTitle>
                    <DialogContent>
                        <Box>
                            <form onSubmit={handleSubmit(addOrEditSection)}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={12} mt={2}>
                                        <AppTextInput control={control} name="Name" label="New section name" />
                                    </Grid>
                                </Grid>
                                <Box display='flex' justifyContent='space-between' sx={{ mt: 3 }}>
                                    <LoadingButton loading={isSubmitting} variant='contained' type="submit" color='primary'>Save</LoadingButton>
                                </Box>
                            </form>
                        </Box >
                    </DialogContent>
                </Dialog>

                <ConfirmationDialog
                    showDialog={showConfirmationDialog}
                    handleAccept={onDelete}
                    handleCancel={handleCloseConfirmation}
                    heading={"Confirm Delete"}
                    message={"Are you sure you want to delete the selected section?"}
                    isLoading={isLoading}
                />

                <EE123InterestModal
                    handleClose={() => {
                        setShowInterestModal(false);
                        history.push('/home');
                    }}
                    showInterestModal={showInterestModal}
                />
            </>
        )
    }
}